import { ref, onMounted } from "vue";
import { useNuxtApp } from "#app";
import { authRepository } from "@/utils/authRepository";
import { useAuthStore } from "@/store/auth"; // Import the store
import type { AuthResponse } from '~/types/types';

export function useAuth() {
  const { $api } = useNuxtApp();
  const authRepo = authRepository($api);
  const authStore = useAuthStore();

  // Load user from  when the composable is used
  onMounted(() => {
    // authStore.loadUserFromStorage();
  });

  // State variables
  const mobile = ref("");
  const newMobile = authStore.mobile;
  const errorMessage = ref("");
  const isLoading = ref(false);
  const codeToken = ref<string | null>(null);

  /**
   * Normalize the mobile number:
   * - If the number has 10 digits and starts with "05", remove the leading "0"
   * - Otherwise, return the number as-is.
   */
  const normalizeMobileNumber = (num: string): string => {
    num = num.trim();
    if (num.length === 10 && num.startsWith("05")) {
      return num.slice(1);
    }
    return num;
  };

  /**
   * Validate the mobile number.
   * - If the normalized number has 9 digits and starts with "5", it is valid.
   * - Otherwise, set an appropriate error message.
   */
  const validateMobile = () => {
    const normalized = normalizeMobileNumber(mobile.value);
    if (normalized.length === 9 && normalized.startsWith("5")) {
      errorMessage.value = "";
    } else {
      errorMessage.value =
        "إدخل رقم جوال صحيح ، يبدأ الرقم بـ 5 او 05";
    }
  };

  /**
   * Handle login API call.
   * The mobile number is normalized before sending to the server.
   */
  const handleLogin = async (): Promise<boolean> => {
    validateMobile();
    if (errorMessage.value) return false;

    const normalizedMobile = normalizeMobileNumber(mobile.value);
    isLoading.value = true;
    errorMessage.value = "";
    try {
      const response: AuthResponse = await authRepo.login(normalizedMobile);
      authStore.setMobile(normalizedMobile);
      
      authStore.setCodeToken(response.code_token);
      return true;
    } catch (error) {
      console.error("❌ فشل تسجيل الدخول:", error);
      errorMessage.value = "حدث خطأ، حاول مرة أخرى.";
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Handle resend code API call.
   * The mobile number is normalized and validated before sending.
   */
  const handleResendCode = async (newMobile: string): Promise<boolean> => {
    const normalizedMobile = normalizeMobileNumber(newMobile);
    if (!(normalizedMobile.length === 9 && normalizedMobile.startsWith("5"))) {
      errorMessage.value = "رقم الجوال غير صحيح.";
      return false;
    }
    try {
      const response: AuthResponse = await authRepo.resendCodeForProfile(normalizedMobile);
      authStore.setMobile(response.phone_number);
        
      authStore.setCodeToken(response.code_token);
      return true;
    } catch (error) {
      console.error("❌ فشل إعادة إرسال الرمز:", error);
      errorMessage.value = "حدث خطأ، حاول مرة أخرى.";
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Handle code verification.
   */
  const handleVerification = async (code: string): Promise<boolean> => {
    let codeToken = useCookie('code_token')
    if (!codeToken.value) {
      errorMessage.value = "رمز الدخول غير موجود. يرجى إعادة المحاولة.";
      return false;
    }
    isLoading.value = true;
    errorMessage.value = "";
    try {
        
      const response = await authRepo.verifyCode(codeToken.value, code);
      authStore.setUser(response);
      codeToken.value = null;

      return true;
    } catch (error) {
      console.error("❌ فشل التحقق من الرمز:", error);
      errorMessage.value = "رمز التحقق غير صحيح";
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Handle profile verification.
   */
  const handleProfileVerification = async (code: string): Promise<boolean> => {
    let codeToken = useCookie('code_token')
    if (!codeToken.value) {
      errorMessage.value = "رمز الدخول غير موجود. يرجى إعادة المحاولة.";
      return false;
    }
    isLoading.value = true;
    errorMessage.value = "";
    try {
      const response = await authRepo.profileConfirmation(codeToken.value, code);
      authStore.setUser(response);
      codeToken.value = null;

      return true;
    } catch (error) {
      console.error("❌ فشل التحقق من الرمز:", error);
      errorMessage.value = "رمز التحقق غير صحيح";
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update user name.
   */
  const updateUserName = async (name: string): Promise<boolean> => {
    try {
      const response = await authRepo.setName(name);
      authStore.setUser(response);
      return true;
    } catch (error) {
      console.error("❌ فشل تحديث الاسم:", error);
      errorMessage.value = "حدث خطأ أثناء تحديث الاسم";
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    mobile,
    newMobile,
    errorMessage,
    isLoading,
    codeToken,
    validateMobile,
    handleLogin,
    updateUserName,
    handleVerification,
    handleResendCode,
    handleProfileVerification,
  };
}
